import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Info, Sparkles } from "lucide-react";

/**
 * Mini-Ampel + Findings-Liste für den Halluzinationscheck im Maßnahmenplan.
 * Sitzt direkt über dem Output. Default eingeklappt (Banner + CTA),
 * öffnet eine Detail-Liste mit „Zum Hinweis springen“-Buttons.
 *
 * Props:
 *  - halluz: Backend-Response. Zwei Modi:
 *    a) Voller Zugriff (Pro+): { severity, count, highest, label, findings, tier_full: true }
 *    b) Teaser (Single-Held): { tier_full: false, teaser: true, upsell_bullets, upsell_text, has_hinweise, label, tier_required }
 *  - onJumpTo?: (regel, severity) => void
 *  - onUpgradeClick?: () => void
 */
const SEV_STYLE = {
  gruen: {
    wrap: "bg-emerald-50 border-emerald-300 text-emerald-900",
    icon: CheckCircle2,
    iconCls: "text-emerald-600",
    badge: "bg-emerald-200 text-emerald-900",
  },
  gelb: {
    wrap: "bg-amber-50 border-amber-300 text-amber-900",
    icon: Info,
    iconCls: "text-amber-600",
    badge: "bg-amber-200 text-amber-900",
  },
  rot: {
    wrap: "bg-red-50 border-red-300 text-red-900",
    icon: AlertTriangle,
    iconCls: "text-red-600",
    badge: "bg-red-200 text-red-900",
  },
};

const KAT_LABEL = {
  widerspruch: "Widerspruch zur SIS",
  sensibel: "Rechtlich sensibel",
  annahme: "KI-Annahme",
};

const FINDING_SEV_STYLE = {
  widerspruch: "border-red-300 bg-red-50",
  sensibel: "border-red-300 bg-red-50",
  annahme: "border-amber-300 bg-amber-50",
};

export default function PkiHalluzinationsAmpel({ halluz, onJumpTo, onUpgradeClick }) {
  const [open, setOpen] = useState(false);
  if (!halluz) return null;

  // --- Teaser-Modus (Single-Held 49€) ---
  if (halluz.teaser || halluz.tier_full === false) {
    const bullets = (halluz.upsell_bullets && halluz.upsell_bullets.length)
      ? halluz.upsell_bullets
      : [
          "Widersprüche zwischen SIS und Maßnahmenplan",
          "Fehlende Maßnahmen bei Risiken",
          "Unvollständige Begründungen",
          "Kritische Dokumentationslücken",
        ];
    return (
      <div
        data-testid="pki-halluz-teaser"
        data-tier-required={halluz.tier_required || "Pflege-KI"}
        className="rounded-2xl border-2 border-dashed border-terra/50 bg-peach-50 p-4 mb-3"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <Sparkles className="h-5 w-5 mt-0.5 shrink-0 text-terra" strokeWidth={2} />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-brand-darkred">
                Automatische Plausibilitätsprüfung verfügbar
              </div>
              <p className="text-[13.5px] text-sand-800 leading-snug mt-1 max-w-2xl">
                Die Pflege-KI prüft jeden Maßnahmenplan automatisch auf typische
                Schwachstellen und macht Hinweise sichtbar, bevor du freigibst.
              </p>
              <ul className="mt-2 space-y-1 text-[13px] text-sand-700 leading-snug">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-terra font-bold shrink-0">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2.5 text-[11.5px] uppercase tracking-[0.18em] font-semibold text-sand-500">
                Letzte Prüfung: <span className="text-brand-darkred">Upgrade erforderlich</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="pki-halluz-upgrade"
            onClick={() => (onUpgradeClick ? onUpgradeClick() : window.location.assign("/preise"))}
            className="h-10 px-4 rounded-full font-semibold text-[13px] bg-brand-darkred hover:bg-brand-darkred/90 text-white inline-flex items-center gap-1.5"
          >
            Pflege-KI freischalten
          </button>
        </div>
      </div>
    );
  }

  // --- Voller Modus (Pro+) ---
  const severity = halluz.severity || "gruen";
  const count = halluz.count || 0;
  const findings = halluz.findings || [];
  const label = halluz.label || "Plausibilitätscheck";
  const style = SEV_STYLE[severity] || SEV_STYLE.gruen;
  const Icon = style.icon;
  // Begründbarkeits-Ampel (nur Höherstufung & Kassenbrief)
  const begr = halluz.begruendbarkeit;

  return (
    <>
      {/* Begründbarkeits-Ampel (nur Höherstufung & Kassenbrief) */}
      {begr && (
        <div
          data-testid="pki-begruendbarkeit-ampel"
          data-ampel={begr.ampel || "gruen"}
          className={`rounded-2xl border p-4 mb-3 ${
            begr.ampel === "rot"   ? "bg-red-50 border-red-300 text-red-900" :
            begr.ampel === "gelb"  ? "bg-amber-50 border-amber-300 text-amber-900" :
                                      "bg-emerald-50 border-emerald-300 text-emerald-900"
          }`}
        >
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold opacity-80">
            Pflegefachliche Einschätzung Höherstufung
          </div>
          <div className="font-display text-[15.5px] leading-snug mt-0.5">
            {begr.label}
          </div>
          {begr.kurzbegruendung && (
            <p className="text-[12.5px] leading-snug mt-1 opacity-90">
              {begr.kurzbegruendung}
            </p>
          )}
          {begr.ampel === "rot" && (
            <p className="text-[12px] leading-snug mt-2 font-semibold">
              Empfehlung: „Erneute Überprüfung der Pflegegradeinstufung beantragt.“ schreiben,
              statt einen konkreten Pflegegrad zu fordern.
            </p>
          )}
        </div>
      )}

    <div
      data-testid="pki-halluz-ampel"
      data-severity={severity}
      data-count={count}
      className={`rounded-2xl border ${style.wrap} p-4 mb-3`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${style.iconCls}`} strokeWidth={2} />
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold opacity-80">
              Automatischer Plausibilitätscheck
            </div>
            <div className="font-display text-[15.5px] leading-snug mt-0.5">
              {label}
            </div>
            {count > 0 && (
              <div className="mt-1 flex items-center gap-2 flex-wrap text-[12px]">
                <span className={`inline-flex h-5 px-2 items-center rounded-full font-bold ${style.badge}`}>
                  {count} Hinweis{count === 1 ? "" : "e"}
                </span>
                {halluz.highest && (
                  <span className="opacity-80">
                    Höchste Kategorie: <strong>{KAT_LABEL[halluz.highest] || halluz.highest}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {count > 0 && (
          <button
            type="button"
            data-testid="pki-halluz-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`h-9 px-3 rounded-full font-semibold text-[12.5px] inline-flex items-center gap-1.5 transition-colors ${
              severity === "rot"
                ? "bg-red-700 hover:bg-red-800 text-white"
                : "bg-amber-700 hover:bg-amber-800 text-white"
            }`}
          >
            {open ? "Hinweise verbergen" : "Hinweise anzeigen"}
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      {open && findings.length > 0 && (
        <ol data-testid="pki-halluz-findings" className="mt-3 space-y-2 list-none">
          {findings.map((f, i) => {
            const fSev = f.severity || "annahme";
            const fStyle = FINDING_SEV_STYLE[fSev] || FINDING_SEV_STYLE.annahme;
            return (
              <li
                key={`${f.regel}-${i}`}
                data-testid={`pki-halluz-finding-${i}`}
                className={`rounded-xl border ${fStyle} p-3`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="text-[12px] uppercase tracking-[0.2em] font-bold opacity-75">
                    {i + 1}. {KAT_LABEL[fSev] || fSev} · {f.regel}
                  </div>
                  {onJumpTo && (
                    <button
                      type="button"
                      data-testid={`pki-halluz-jump-${i}`}
                      onClick={() => onJumpTo(f.regel, fSev)}
                      className="h-7 px-2 rounded-full text-[11px] font-semibold bg-white border border-current opacity-80 hover:opacity-100"
                    >
                      Zum Hinweis springen
                    </button>
                  )}
                </div>
                <div className="mt-1.5 text-[13px] leading-snug text-sand-800">
                  <div><strong>Hinweis:</strong> {f.gefunden}</div>
                  {f.warum && (
                    <div className="mt-0.5"><strong>Warum prüfen?</strong> {f.warum}</div>
                  )}
                  <div className="mt-0.5"><strong>Empfehlung:</strong> {f.hinweis}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
    </>
  );
}
